<template>
  <v-app>
    <v-app-bar
      v-if="authStore.isImpersonating"
      color="warning"
      density="compact"
      order="-1"
      :elevation="2"
    >
      <v-icon class="mx-3">mdi-account-eye</v-icon>
      <v-app-bar-title>
        Impersonating <strong>{{ authStore.user?.userName }}</strong>
        <template v-if="authStore.impersonator">
          &nbsp;&middot;&nbsp;signed in as {{ authStore.impersonator.name }}
        </template>
      </v-app-bar-title>
      <v-spacer />
      <v-chip
        v-if="testProfileLabel"
        class="mr-2"
        variant="flat"
        color="white"
        prepend-icon="mdi-flask"
        size="small"
      >
        Testing as {{ testProfileLabel }}
      </v-chip>
      <v-btn
        variant="text"
        class="mr-1"
        prepend-icon="mdi-badge-account"
        @click="testProfileOpen = true"
      >
        Test profile
      </v-btn>
      <v-btn
        variant="elevated"
        class="mr-3"
        :loading="stopping"
        @click="stopImpersonating"
      >
        Return to my account
      </v-btn>
    </v-app-bar>
    <TestProfileDialog
      v-if="authStore.isImpersonating"
      v-model="testProfileOpen"
    />
    <router-view />
  </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import TestProfileDialog from "@/components/TestProfileDialog.vue";

const router = useRouter();
const authStore = useAuthStore();
const stopping = ref(false);
const testProfileOpen = ref(false);

const testProfileLabel = computed(() => {
  const p = authStore.testProfile;
  if (!p) return null;
  return [p.jobTitle, p.locationName].filter(Boolean).join(" at ");
});

async function stopImpersonating() {
  stopping.value = true;
  try {
    await authStore.stopImpersonation();
    await router.push(authStore.adminLandingRoute);
  } finally {
    stopping.value = false;
  }
}

onMounted(async () => {
  // Hydrate auth state so the impersonation banner is reliable across refreshes.
  try {
    await authStore.authenticated();
  } catch {
    // Ignore: unauthenticated visitors (e.g. the login page) have no session.
  }
});
</script>
